import React, { useEffect, useMemo, useState } from 'react';
import { useClientPayouts } from './useClientPayouts';
import ClientPayoutsTable, { ClientPayoutProductTab } from './ClientPayoutsTable';
import { useDelay } from '../../hooks/useDelay';
import TableSkeleton from '../../components/TableSkeleton';
import Input from '../../components/Fields/Input';
import { SearchOutlined } from '@mui/icons-material';
import { debounce } from '../../utils/debounce';
import dayjs from 'dayjs';
import toast from 'react-hot-toast';
import { Tab, Tabs } from '@mui/material';
import { useProfile } from '../../context/ProfileContext';
import DatePickerField from '../../components/DatePickerField';

const ClientPayouts: React.FC = () => {
  const { profileData } = useProfile();
  const enableInvoiceDiscounting = profileData?.productTypes?.enableInvoiceDiscounting !== false;
  const enableAssetLeasing = profileData?.productTypes?.enableAssetLeasing !== false;

  const defaultTab: ClientPayoutProductTab = enableInvoiceDiscounting
    ? 'INVOICE_DISCOUNTING'
    : 'ASSET_LEASING';

  const [productTab, setProductTab] = useState<ClientPayoutProductTab>(defaultTab);
  const [pageSize, setPageSize] = useState(10);
  const [pageNo, setPageNo] = useState(0);
  const [orderBy, setOrderBy] = useState('payoutDate');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [search, setSearch] = useState('');
  const [fromDate, setFromDate] = useState(() =>
    dayjs().subtract(30, 'day').format('YYYY-MM-DD'),
  );
  const [toDate, setToDate] = useState(() => dayjs().format('YYYY-MM-DD'));

  useEffect(() => {
    if (productTab === 'INVOICE_DISCOUNTING' && !enableInvoiceDiscounting && enableAssetLeasing) {
      setProductTab('ASSET_LEASING');
    }
    if (productTab === 'ASSET_LEASING' && !enableAssetLeasing && enableInvoiceDiscounting) {
      setProductTab('INVOICE_DISCOUNTING');
    }
  }, [enableInvoiceDiscounting, enableAssetLeasing, productTab]);

  const filters = useMemo(() => {
    const next: Array<{ field: string; operation: string; value: string }> = [];
    const toApiDateTime = (date: string, endOfDay = false) =>
      endOfDay ? `${date}T23:59:59+05:30` : `${date}T00:00:00+05:30`;

    next.push({
      field: 'dealType',
      operation: 'EQUALS',
      value: productTab,
    });

    if (fromDate) {
      next.push({
        field: 'payoutDate',
        operation: 'GREATER_THAN_OR_EQUALS',
        value: toApiDateTime(fromDate),
      });
    }
    if (toDate) {
      next.push({
        field: 'payoutDate',
        operation: 'LESSER_THAN_OR_EQUALS',
        value: toApiDateTime(toDate, true),
      });
    }
    return next;
  }, [fromDate, toDate, productTab]);

  const { data, isLoading, isError } = useClientPayouts({
    payload: {
      pageNo,
      pageSize,
      orderBy,
      order,
      search,
      filters,
    },
  });

  const isReady = useDelay(isLoading, 400);

  useEffect(() => {
    if (isError) {
      toast.error('Unable to load client payouts. Please try again.');
    }
  }, [isError]);

  const handleTabChange = (_event, newValue: ClientPayoutProductTab) => {
    setProductTab(newValue);
    setPageNo(0);
  };

  const handleChangePage = (_event, newPage) => {
    setPageNo(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setPageSize(parseInt(event.target.value, 10));
    setPageNo(0);
  };

  const handleChangeSort = (_e, columnId) => {
    if (columnId === orderBy) {
      setOrder(order === 'desc' ? 'asc' : 'desc');
    } else {
      setOrderBy(columnId);
      setOrder('desc');
    }
    setPageNo(0);
  };

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPageNo(0);
  };

  const debounceHandleSearch = debounce(handleSearch, 500);

  return (
    <div className="px-4 py-6 max-w-7xl mx-auto">
      <div className="mb-4">
        <h1 className="text-2xl font-semibold text-slate-900">Client Payouts</h1>
        <p className="text-sm text-slate-600 mt-1">
          Scheduled / expected client maturity payouts based on investment schedules. This does not
          confirm that money has already been transferred to the client.
        </p>
      </div>

      <Tabs
        className="mt-2 max-w-7xl"
        classes={{
          indicator: '!bg-green-700',
          flexContainer: 'border-b border-b-black/[0.08]',
        }}
        value={productTab}
        onChange={handleTabChange}
        aria-label="client-payouts-tabs"
      >
        {enableInvoiceDiscounting && (
          <Tab
            label="Invoice Discounting"
            value="INVOICE_DISCOUNTING"
            className="!capitalize !font-medium"
            classes={{ selected: '!text-green-700' }}
          />
        )}
        {enableAssetLeasing && (
          <Tab
            label="Asset Leasing"
            value="ASSET_LEASING"
            className="!capitalize !font-medium"
            classes={{ selected: '!text-green-700' }}
          />
        )}
      </Tabs>

      <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between my-6">
        <div className="w-full md:max-w-sm">
          <Input
            placeholder="Search client name, phone, or deal name"
            onChange={debounceHandleSearch}
            prefixIcon={<SearchOutlined />}
          />
        </div>
        <div className="flex flex-wrap gap-3 items-end">
          <DatePickerField
            label="Expected payout from"
            value={fromDate}
            max={toDate || undefined}
            onChange={(value) => {
              setFromDate(value);
              setPageNo(0);
            }}
          />
          <DatePickerField
            label="Expected payout to"
            value={toDate}
            min={fromDate || undefined}
            onChange={(value) => {
              setToDate(value);
              setPageNo(0);
            }}
          />
        </div>
      </div>

      {!isReady ? (
        <TableSkeleton />
      ) : (
        <ClientPayoutsTable
          data={data?.data}
          productTab={productTab}
          handleChangePage={handleChangePage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          handleChangeSort={handleChangeSort}
          orderBy={orderBy}
          order={order}
          pageSize={pageSize}
          pageNo={pageNo}
          loading={isLoading}
        />
      )}
    </div>
  );
};

export default ClientPayouts;
